/**
 * Connection Detection Service
 * 
 * Provides functionality to check if an organization has any active connections.
 * Used to determine whether to route new users to connection setup or app orientation flow.
 */

import { connectionsAPI } from '../api/enhancedApiService';

export class ConnectionDetectionService {
  constructor() {
    this.cache = null;
    this.cacheTimestamp = null;
    this.CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Check if the organization has any active connections
   * @param {Object} options - Options for the check
   * @param {boolean} options.forceFresh - Whether to bypass cache and fetch fresh data
   * @returns {Promise<boolean>} True if connections exist, false otherwise
   */
  async hasConnections(options = {}) {
    const { forceFresh = false } = options;

    try {
      // Check cache first unless forcing fresh
      if (!forceFresh && this.isCacheValid()) {
        console.log('[ConnectionDetection] Using cached connection check result:', !!this.cache?.length);
        return !!this.cache?.length;
      }

      console.log('[ConnectionDetection] Fetching connections to check existence...');
      
      // Fetch connections from API
      const response = await connectionsAPI.getAll({ forceFresh: true });
      
      // Extract connections array from response
      let connections = [];
      if (response?.data?.connections) {
        connections = response.data.connections;
      } else if (response?.connections) {
        connections = response.connections;
      } else if (Array.isArray(response)) {
        connections = response;
      }

      // Update cache
      this.cache = connections;
      this.cacheTimestamp = Date.now();

      const hasConnections = connections.length > 0;
      console.log(`[ConnectionDetection] Found ${connections.length} connections:`, hasConnections);
      
      return hasConnections;

    } catch (error) {
      console.error('[ConnectionDetection] Error checking connections:', error);
      
      // Check if this is a network/CORS error
      const isNetworkError = error.message?.includes('Network Error') || 
                             error.code === 'ERR_NETWORK' ||
                             error.message?.includes('CORS');
      
      // If we have cached data, use it as fallback
      if (this.cache !== null) {
        console.log('[ConnectionDetection] Using cached data as fallback');
        return !!this.cache?.length;
      }
      
      if (isNetworkError) {
        console.log('[ConnectionDetection] Network error detected - assuming connections exist to avoid onboarding loop');
        // For network errors, assume connections exist to prevent routing to onboarding
        // This is safer than getting stuck in onboarding due to temporary API issues
        return true;
      }
      
      // Default to false if no connections can be determined and it's not a network error
      // This will route users to connection setup, which is safer for business logic errors
      return false;
    }
  }

  /**
   * Get the count of active connections
   * @param {Object} options - Options for the check
   * @returns {Promise<number>} Number of active connections
   */
  async getConnectionCount(options = {}) {
    const { forceFresh = false } = options;

    try {
      // Use the same caching logic as hasConnections
      if (!forceFresh && this.isCacheValid()) {
        return this.cache?.length || 0;
      }

      const response = await connectionsAPI.getAll({ forceFresh: true });
      
      let connections = [];
      if (response?.data?.connections) {
        connections = response.data.connections;
      } else if (response?.connections) {
        connections = response.connections;
      } else if (Array.isArray(response)) {
        connections = response;
      }

      this.cache = connections;
      this.cacheTimestamp = Date.now();

      return connections.length;

    } catch (error) {
      console.error('[ConnectionDetection] Error getting connection count:', error);
      return this.cache?.length || 0;
    }
  }

  /**
   * Get basic info about existing connections (for welcome message)
   * @returns {Promise<Array>} Array of connection info objects
   */
  async getConnectionSummary() {
    try {
      const response = await connectionsAPI.getAll();
      
      let connections = [];
      if (response?.data?.connections) {
        connections = response.data.connections;
      } else if (response?.connections) {
        connections = response.connections;
      } else if (Array.isArray(response)) {
        connections = response;
      }

      // Return summary info for display purposes
      return connections.map(conn => ({
        id: conn.id,
        name: conn.name,
        type: conn.connection_type || 'Unknown',
        status: conn.status || 'active',
        created_at: conn.created_at
      }));

    } catch (error) {
      console.error('[ConnectionDetection] Error getting connection summary:', error);
      return [];
    }
  }

  /**
   * Clear the cache (useful for testing or when connections are modified)
   */
  clearCache() {
    this.cache = null;
    this.cacheTimestamp = null;
    console.log('[ConnectionDetection] Cache cleared');
  }

  /**
   * Check if the cached data is still valid
   * @returns {boolean} True if cache is valid
   */
  isCacheValid() {
    if (this.cache === null || this.cacheTimestamp === null) {
      return false;
    }
    
    const cacheAge = Date.now() - this.cacheTimestamp;
    return cacheAge < this.CACHE_DURATION;
  }
}

// Export a singleton instance
export const connectionDetection = new ConnectionDetectionService();

// Export the class for testing purposes
export default ConnectionDetectionService;